import React, { useMemo, useRef, useState, useCallback } from "react";

/* ───────────────────────────────────────────────────────────────────────────
 * LcsHeroChart.js — atmospheric SVG hero chart for the LCS decision cockpit.
 *
 * Visual lineage: ForecastCockpit's HeroChart (haze + glow + filament curves,
 * forecast region shading, TODAY beam, subtle gridlines).
 *
 * Scaling model: PER-STREAM LANES.
 *   Each present stream gets its own horizontal band that fills [P5..P95×1.15]
 *   on a log scale. This prevents a single COIB-style 11,963 bbl/d water
 *   spike from squishing every other stream into a thin band at the bottom.
 *   Tooltip / KPI strip / right-rail evidence still use raw values.
 *
 * Lane order (top → bottom): Gas, Water, Oil. Streams with no positive
 * actuals are dropped from the layout so 1-stream / 2-stream leases get
 * the full canvas.
 * ──────────────────────────────────────────────────────────────────────── */

const VB_X0 = 60, VB_X1 = 760, VB_W = 700;
const VB_TOP = 30, VB_BOT = 370, VB_H = 340;
const LANE_GAP = 10;          // px between lanes in viewBox units
const LANE_PAD_TOP = 6;       // padding inside each lane
const LANE_PAD_BOT = 6;

const STREAM_ORDER = ["Gas", "Water", "Oil"]; // top → bottom

const STREAM_COLORS = {
  Oil:   { core: "#2a8a62", filament: "#5afcb8", glow: "#3cc888", bandTop: "#3ee8a0", bandMid: "#1a9060" },
  Gas:   { core: "#a85050", filament: "#f09898", glow: "#d07070", bandTop: "#f08888", bandMid: "#a04848" },
  Water: { core: "#4070a8", filament: "#80c0f0", glow: "#5a90d0", bandTop: "#70b0e8", bandMid: "#3870a0" },
};

function pathFrom(points) {
  if (!points || points.length < 2) return "";
  const valid = points.filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (valid.length < 2) return "";
  let d = `M${valid[0][0].toFixed(1)},${valid[0][1].toFixed(1)}`;
  for (let i = 1; i < valid.length; i++) {
    const [px, py] = valid[i - 1];
    const [cx, cy] = valid[i];
    const dx = (cx - px) * 0.4;
    d += ` C${(px + dx).toFixed(1)},${py.toFixed(1)} ${(cx - dx).toFixed(1)},${cy.toFixed(1)} ${cx.toFixed(1)},${cy.toFixed(1)}`;
  }
  return d;
}

function _percentile(arr, p) {
  if (!arr.length) return null;
  const sorted = arr.slice().sort((a, b) => a - b);
  const i = Math.max(0, Math.min(sorted.length - 1, Math.round(p * (sorted.length - 1))));
  return sorted[i];
}

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return d; }
}

function fmtNum(n, dp = 1) {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1000) return Math.round(n).toLocaleString();
  return n.toFixed(dp);
}

const STREAM_UNIT = { Oil: "bbl/d", Gas: "mcf/d", Water: "bbl/d" };

export default function LcsHeroChart({
  view,
  showOil = true,
  showGas = true,
  showWater = true,
  showBands = true,
  showMA = true,
  loading = false,
  emptyMessage = "Select a lease to load forecast.",
}) {
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null);

  const handleMove = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const rx = (e.clientX - rect.left) / rect.width;
    const svgX = VB_X0 + rx * VB_W;
    if (svgX < VB_X0 || svgX > VB_X1) { setHover(null); return; }
    setHover({ px: e.clientX - rect.left, py: e.clientY - rect.top, svgX });
  }, []);
  const handleLeave = useCallback(() => setHover(null), []);

  const layout = useMemo(() => {
    if (!view || !view.raw) return null;
    const raw = view.raw;

    // ── 1) Date axis (union of all streams' dates).
    const dateSet = new Set();
    let lastActualDate = null;
    for (const s of ["Oil", "Gas", "Water"]) {
      for (const p of raw.actual?.[s] || []) {
        if (p.date) {
          dateSet.add(p.date);
          if (!lastActualDate || p.date > lastActualDate) lastActualDate = p.date;
        }
      }
      for (const p of raw.forecast?.[s] || []) if (p.date) dateSet.add(p.date);
    }
    const dateAxis = [...dateSet].sort();
    if (dateAxis.length < 2) return null;

    const xOf = (date) => {
      const i = dateAxis.indexOf(date);
      if (i < 0) return null;
      return VB_X0 + (i / Math.max(1, dateAxis.length - 1)) * VB_W;
    };
    const todayX = lastActualDate ? (xOf(lastActualDate) ?? VB_X0 + 0.7 * VB_W) : VB_X0 + 0.7 * VB_W;

    // ── 2) Determine which streams are PRESENT (have any positive actual or forecast).
    const present = STREAM_ORDER.filter((s) => {
      const want = (s === "Oil" && showOil) || (s === "Gas" && showGas) || (s === "Water" && showWater);
      if (!want) return false;
      const aHas = (raw.actual?.[s] || []).some((p) => Number.isFinite(p.q) && p.q > 0);
      const fHas = (raw.forecast?.[s] || []).some((p) => Number.isFinite(p.p50) && p.p50 > 0);
      return aHas || fHas;
    });
    if (present.length === 0) return { empty: true };

    // ── 3) Allocate lanes. Each present stream gets an equal vertical band.
    const totalGaps = LANE_GAP * (present.length - 1);
    const laneH = (VB_H - totalGaps) / present.length;
    const lanes = {};
    present.forEach((s, idx) => {
      const top = VB_TOP + idx * (laneH + LANE_GAP);
      const bot = top + laneH;
      lanes[s] = { idx, top, bot, height: laneH };
    });

    // ── 4) Per-stream robust bounds + yScale within its lane.
    const series = {};
    for (const s of present) {
      const aRows = raw.actual?.[s] || [];
      const fRows = raw.forecast?.[s] || [];
      const positives = [];
      for (const p of aRows) if (Number.isFinite(p.q) && p.q > 0) positives.push(p.q);
      for (const p of fRows) {
        if (Number.isFinite(p.p50) && p.p50 > 0) positives.push(p.p50);
        if (Number.isFinite(p.p90) && p.p90 > 0) positives.push(p.p90);
      }
      let lo = _percentile(positives, 0.05) ?? 0.5;
      let hi = _percentile(positives, 0.95) ?? lo * 2;
      if (!(hi > lo)) hi = lo * 1.5 + 0.1;
      // Visual padding — winsorize bottom + top so peaks don't kiss the lane edge.
      const visLo = Math.max(0.0001, lo * 0.8);
      const visHi = hi * 1.15;
      const logLo = Math.log10(visLo);
      const logHi = Math.log10(visHi);

      const lane = lanes[s];
      const usableTop = lane.top + LANE_PAD_TOP;
      const usableBot = lane.bot - LANE_PAD_BOT;
      const usableH = usableBot - usableTop;

      const yScale = (v) => {
        if (!Number.isFinite(v) || v <= 0) return usableBot;
        const clamped = Math.min(Math.max(v, visLo), visHi);
        const t = (Math.log10(clamped) - logLo) / (logHi - logLo);
        return usableBot - t * usableH;
      };

      // Build hover/value points + paths.
      const histPts = [];
      const fcstPts = [];
      const bandUpper = [];
      const bandLower = [];
      for (const p of aRows) {
        const x = xOf(p.date);
        if (x == null || !Number.isFinite(p.q) || p.q <= 0) continue;
        histPts.push([x, yScale(p.q), p.q, p.date]);
      }
      for (const p of fRows) {
        const x = xOf(p.date);
        if (x == null) continue;
        if (Number.isFinite(p.p50) && p.p50 > 0) fcstPts.push([x, yScale(p.p50), p.p50, p.date]);
        if (Number.isFinite(p.p10) && Number.isFinite(p.p90) && p.p10 > 0 && p.p90 > 0) {
          bandLower.push([x, yScale(p.p10)]);
          bandUpper.push([x, yScale(p.p90)]);
        }
      }
      // 7-day moving average on history values.
      const maPts = [];
      for (let i = 0; i < histPts.length; i++) {
        const start = Math.max(0, i - 6);
        let sum = 0, n = 0;
        for (let j = start; j <= i; j++) { sum += histPts[j][2]; n++; }
        if (n) maPts.push([histPts[i][0], yScale(sum / n)]);
      }
      series[s] = {
        histPath: pathFrom(histPts.map((p) => [p[0], p[1]])),
        fcstPath: pathFrom(fcstPts.map((p) => [p[0], p[1]])),
        maPath: pathFrom(maPts),
        bandUpper: pathFrom(bandUpper),
        bandLower: pathFrom(bandLower.slice().reverse()),
        hover: histPts.concat(fcstPts), // [x, y, v, date]
        visLo, visHi, lane,
      };
    }

    return { dateAxis, todayX, present, lanes, series };
  }, [view, showOil, showGas, showWater]);

  const hoverData = useMemo(() => {
    if (!hover || !layout || layout.empty) return null;
    const items = [];
    for (const s of layout.present || []) {
      const ser = layout.series[s];
      if (!ser || !ser.hover.length) continue;
      let nearest = ser.hover[0];
      let dx = Math.abs(nearest[0] - hover.svgX);
      for (const p of ser.hover) {
        const d = Math.abs(p[0] - hover.svgX);
        if (d < dx) { dx = d; nearest = p; }
      }
      items.push({
        stream: s, x: nearest[0], y: nearest[1], v: nearest[2], date: nearest[3],
        color: STREAM_COLORS[s].filament, unit: STREAM_UNIT[s],
      });
    }
    return items.length ? { items, date: items[0].date } : null;
  }, [hover, layout]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="ck-hero" ref={containerRef} style={{ position: "relative", aspectRatio: "2 / 1", maxHeight: "min(58vh, 560px)", flex: "0 0 auto", width: "100%" }}>
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          color: "#566580", fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
          letterSpacing: "0.04em",
        }}>
          Loading overlay…
        </div>
      </div>
    );
  }

  if (!layout || layout.empty) {
    return (
      <div className="ck-hero" ref={containerRef} style={{ position: "relative", aspectRatio: "2 / 1", maxHeight: "min(58vh, 560px)", flex: "0 0 auto", width: "100%" }}>
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          color: "#566580", fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
          letterSpacing: "0.04em", textAlign: "center", padding: "0 40px",
        }}>
          {emptyMessage}
        </div>
      </div>
    );
  }

  const { todayX, present, lanes, series } = layout;
  const isHover = hover != null;
  const dimWhenHover = isHover ? 0.45 : 1;

  return (
    <div
      className="ck-hero"
      ref={containerRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{
        // Pin chart to native viewBox aspect (800×400 = 2:1) so it doesn't
        // get stretched anisotropically when the parent flex column is tall.
        // Cap with max-height so on very tall viewports it doesn't exceed
        // ~58vh. This keeps side rails visually proportional.
        aspectRatio: "2 / 1",
        maxHeight: "min(58vh, 560px)",
        flex: "0 0 auto",
        width: "100%",
      }}
    >
      <svg className="ck-hero-svg" viewBox="0 0 800 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id="lhc-bG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={STREAM_COLORS.Oil.bandTop} stopOpacity="0.30" />
            <stop offset="40%" stopColor={STREAM_COLORS.Oil.bandMid} stopOpacity="0.08" />
            <stop offset="100%" stopColor={STREAM_COLORS.Oil.bandMid} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lhc-bR" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={STREAM_COLORS.Gas.bandTop} stopOpacity="0.24" />
            <stop offset="40%" stopColor={STREAM_COLORS.Gas.bandMid} stopOpacity="0.06" />
            <stop offset="100%" stopColor={STREAM_COLORS.Gas.bandMid} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lhc-bB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={STREAM_COLORS.Water.bandTop} stopOpacity="0.26" />
            <stop offset="40%" stopColor={STREAM_COLORS.Water.bandMid} stopOpacity="0.06" />
            <stop offset="100%" stopColor={STREAM_COLORS.Water.bandMid} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lhc-tH" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#60c8d8" stopOpacity="0" />
            <stop offset="35%" stopColor="#60c8d8" stopOpacity="0.04" />
            <stop offset="50%" stopColor="#78e0f0" stopOpacity="0.10" />
            <stop offset="65%" stopColor="#60c8d8" stopOpacity="0.04" />
            <stop offset="100%" stopColor="#60c8d8" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="lhc-atm" cx="48%" cy="28%" r="42%">
            <stop offset="0%" stopColor="#182c58" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#0c1830" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lhc-fcst" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#3060a0" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#2050a0" stopOpacity="0.02" />
          </linearGradient>
          <filter id="lhc-f4"><feGaussianBlur stdDeviation="4" /></filter>
          <filter id="lhc-f8"><feGaussianBlur stdDeviation="8" /></filter>
          <filter id="lhc-f2g"><feGaussianBlur stdDeviation="2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="lhc-f3g"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>

        {/* atmospheric backdrop */}
        <rect width="800" height="400" fill="url(#lhc-atm)" />

        {/* very faint horizontal grid lines per lane */}
        {present.map((s) => {
          const lane = lanes[s];
          const mid = (lane.top + lane.bot) / 2;
          return (
            <g key={`grid-${s}`}>
              <line x1="40" y1={lane.top + 1} x2="760" y2={lane.top + 1} stroke="rgba(20,38,72,0.18)" strokeWidth="0.5" strokeDasharray="2,8" />
              <line x1="40" y1={mid} x2="760" y2={mid} stroke="rgba(20,38,72,0.10)" strokeWidth="0.4" strokeDasharray="2,12" />
              <line x1="40" y1={lane.bot - 1} x2="760" y2={lane.bot - 1} stroke="rgba(20,38,72,0.18)" strokeWidth="0.5" strokeDasharray="2,8" />
            </g>
          );
        })}

        {/* date gridlines (vertical) */}
        {[120, 240, 360, 480, 600, 720].map((x) => (
          <line key={x} x1={x} y1="20" x2={x} y2="380" stroke="rgba(20,38,72,0.08)" strokeWidth="0.4" strokeDasharray="3,12" />
        ))}

        {/* forecast region shading. The "FORECAST" label is rendered as an
            HTML overlay below — keeping <text> inside the SVG would get
            vertically stretched anisotropically by preserveAspectRatio="none". */}
        <rect x={todayX} y="0" width={Math.max(0, VB_X1 - todayX)} height="400" fill="url(#lhc-fcst)" />

        {/* TODAY beam */}
        <rect x={todayX - 80} y="0" width="160" height="400" fill="url(#lhc-tH)" opacity={dimWhenHover} />
        <line x1={todayX} y1="0" x2={todayX} y2="400" stroke="#88e8f8" strokeWidth="4" opacity={isHover ? 0.05 : 0.14} filter="url(#lhc-f4)" />
        <line x1={todayX} y1="0" x2={todayX} y2="400" stroke="#b0f4ff" strokeWidth="1.2" opacity={isHover ? 0.4 : 0.75} />

        {/* per-stream lanes — bands, MA, curves */}
        {present.map((s) => {
          const ser = series[s];
          const C = STREAM_COLORS[s];
          const bandFill = s === "Oil" ? "url(#lhc-bG)" : s === "Gas" ? "url(#lhc-bR)" : "url(#lhc-bB)";
          return (
            <g key={`stream-${s}`}>
              {/* confidence band */}
              {showBands && ser.bandUpper && ser.bandLower && (
                <g style={{ mixBlendMode: "screen" }}>
                  <path d={`${ser.bandUpper} ${ser.bandLower} Z`} fill={bandFill} opacity="0.6" />
                </g>
              )}
              {/* 7d MA */}
              {showMA && ser.maPath && (
                <path d={ser.maPath} fill="none" stroke={C.core} strokeWidth="1.5" opacity="0.4" strokeDasharray="4,3" />
              )}
              {/* history: haze + glow + filament */}
              {ser.histPath && (
                <>
                  <path d={ser.histPath} fill="none" stroke={C.core} strokeWidth="11" opacity="0.10" filter="url(#lhc-f8)" />
                  <path d={ser.histPath} fill="none" stroke={C.glow} strokeWidth="3.5" opacity="0.22" filter="url(#lhc-f3g)" />
                  <path d={ser.histPath} fill="none" stroke={C.filament} strokeWidth="1.8" opacity={isHover ? 0.6 : 0.88} filter="url(#lhc-f2g)" />
                </>
              )}
              {/* forecast: dashed filament */}
              {ser.fcstPath && (
                <>
                  <path d={ser.fcstPath} fill="none" stroke={C.glow} strokeWidth="5" opacity="0.05" filter="url(#lhc-f8)" />
                  <path d={ser.fcstPath} fill="none" stroke={C.filament} strokeWidth="1.3" strokeDasharray="6,4" opacity="0.55" />
                </>
              )}
              {/* (lane label + scale chip rendered as HTML overlays
                   below — see lane-label markup outside the <svg>). */}
            </g>
          );
        })}

        {/* hover crosshair */}
        {isHover && (
          <line x1={hover.svgX} y1="20" x2={hover.svgX} y2="380" stroke="rgba(180,220,250,0.18)" strokeWidth="1" />
        )}
        {isHover && hoverData && hoverData.items.map((it) => (
          <circle key={it.stream} cx={it.x} cy={it.y} r="4"
                  fill={it.color} stroke="rgba(8,14,28,0.9)" strokeWidth="1.5"
                  opacity="0.95" filter="url(#lhc-f2g)" />
        ))}
      </svg>

      {/* HTML overlays — fixed-pixel labels sitting on top of the SVG.
          Positioned in percent of container so they track lane geometry
          regardless of the SVG's anisotropic stretch. */}
      <div style={{
        position: "absolute",
        left: `${(todayX / 800) * 100}%`,
        top: 4,
        marginLeft: 8,
        fontSize: 9, color: "#4a6888", opacity: 0.7,
        fontFamily: "'JetBrains Mono', monospace",
        letterSpacing: "0.04em", pointerEvents: "none",
      }}>FORECAST</div>

      {present.map((s) => {
        const ser = series[s];
        const C = STREAM_COLORS[s];
        const topPct = (ser.lane.top / 400) * 100;
        return (
          <React.Fragment key={`label-${s}`}>
            <div style={{
              position: "absolute",
              left: `${(VB_X0 / 800) * 100}%`,
              top: `${topPct}%`,
              marginLeft: 2, marginTop: 2,
              fontSize: 9, fontWeight: 700, color: C.glow, opacity: 0.65,
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: "0.06em", pointerEvents: "none",
              textShadow: "0 0 6px rgba(0,0,0,0.6)",
            }}>{s.toUpperCase()} · {STREAM_UNIT[s]}</div>
            <div style={{
              position: "absolute",
              right: `${((800 - VB_X1) / 800) * 100}%`,
              top: `${topPct}%`,
              marginRight: 2, marginTop: 2,
              fontSize: 9, color: C.core, opacity: 0.6,
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: "0.04em", pointerEvents: "none",
              textShadow: "0 0 6px rgba(0,0,0,0.6)",
            }}>{fmtNum(ser.visHi, ser.visHi >= 100 ? 0 : 1)}</div>
          </React.Fragment>
        );
      })}

      {/* tooltip */}
      {isHover && hoverData && (
        <div className="ck-tooltip" style={{
          left: Math.min((hover.px || 0) + 14, (containerRef.current?.clientWidth || 800) - 170),
          top: Math.max(8, (hover.py || 0) - 20 - hoverData.items.length * 16),
        }}>
          <div className="ck-tt-date">{fmtDate(hoverData.date)}</div>
          {hoverData.items.map((it) => (
            <div key={it.stream} className="ck-tt-row">
              <span className="ck-tt-dot" style={{ background: it.color, boxShadow: `0 0 6px ${it.color}` }} />
              <span className="ck-tt-name">{it.stream}</span>
              <span className="ck-tt-val">{fmtNum(it.v, 1)} {it.unit}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
